import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Card, CardContent, CardActions,
  Button, Chip, Avatar, Alert, CircularProgress, Grid, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Badge, Tab, Tooltip, MenuItem
} from '@mui/material';
import ScrollableTabs from '@components/mobile/ScrollableTabs';
import MobileFormDialog from '@components/mobile/MobileFormDialog';
import {
  ChildCare as DaycareIcon,
  Login as CheckInIcon,
  Logout as CheckOutIcon,
  Edit as EditIcon,
  AccessTime as ClockIcon,
  CheckCircle as CheckedInIcon,
  Schedule as WaitingIcon,
  NotificationsActive as NotifyIcon,
  StickyNote2 as NoteIcon,
  Send as SendIcon,
  PriorityHigh as UrgentIcon,
  KeyboardArrowUp as HighIcon,
  Visibility as AcknowledgeIcon,
  Done as CompleteIcon,
  CheckCircle as CompleteConfirmIcon,
  WarningAmber as AutoClosedIcon,
  Today as DailyUpdateIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { teacherAPI } from '../../services/api';
import EmptyState from '../../components/EmptyState';

const fmtTime = (ts) => ts ? new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—';
const elapsed = (checkIn) => {
  if (!checkIn) return '';
  const mins = Math.floor((Date.now() - new Date(checkIn)) / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const PRIORITY_COLOR = { urgent: 'error', high: 'warning', normal: 'default' };
const PRIORITY_ICON  = { urgent: <UrgentIcon fontSize="small" />, high: <HighIcon fontSize="small" />, normal: null };
const STATUS_COLOR   = { pending: 'warning', acknowledged: 'info', completed: 'success' };

const today = () => new Date().toISOString().slice(0, 10);
const MOODS = ['Happy', 'Calm', 'Energetic', 'Tired', 'Upset', 'Sick'];
const EMPTY_DAILY_FORM = { student_id: '', update_date: today(), content: '', meals: '', nap: '', mood: '', activities: '' };

export default function DaycareRoster() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState(0);

  // Roster state
  const [roster, setRoster]   = useState([]);
  const [rosterLoading, setRosterLoading] = useState(true);

  // Auto-closed sessions needing review (missed checkout)
  const [autoClosed, setAutoClosed] = useState([]);

  // Care notes state
  const [careNotes, setCareNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);

  // Daily updates state
  const [dailyUpdates, setDailyUpdates] = useState([]);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dailySaving, setDailySaving] = useState(false);
  const [dailyEditId, setDailyEditId] = useState(null);
  const [dailyForm, setDailyForm] = useState(EMPTY_DAILY_FORM);
  const [dailyDeleteId, setDailyDeleteId] = useState(null);

  // Shared
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  // Edit session dialog
  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm]     = useState({ check_in: '', check_out: '' });

  // Notify parent dialog
  const [notifyDialog, setNotifyDialog] = useState(null); // { student_id, student_name }
  const [notifyMsg, setNotifyMsg]       = useState('');
  const [notifySending, setNotifySending] = useState(false);

  // Care note acknowledge/complete
  const [noteActionLoading, setNoteActionLoading] = useState(null);
  const [completeDialog, setCompleteDialog] = useState(null); // { note }
  const [completionRemark, setCompletionRemark] = useState('');

  const loadRoster = useCallback(async () => {
    try {
      const res = await teacherAPI.daycare.getRoster();
      setRoster(res.data.roster || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load daycare roster');
    } finally {
      setRosterLoading(false);
    }
  }, []);

  const loadAutoClosed = useCallback(async () => {
    try {
      const res = await teacherAPI.daycare.getAutoClosed();
      setAutoClosed(res.data.sessions || []);
    } catch {
      // Non-critical — don't block the page on this
    }
  }, []);

  const loadCareNotes = useCallback(async () => {
    setNotesLoading(true);
    try {
      const res = await teacherAPI.daycare.getCareNotes();
      setCareNotes(res.data.notes || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load care notes');
    } finally {
      setNotesLoading(false);
    }
  }, []);

  const loadDailyUpdates = useCallback(async () => {
    setDailyLoading(true);
    try {
      const res = await teacherAPI.dailyUpdates.my({ date: today() });
      const rosterIds = new Set(roster.map(r => r.student_id));
      setDailyUpdates((res.data.updates || []).filter(u => rosterIds.has(u.student_id)));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load daily updates');
    } finally {
      setDailyLoading(false);
    }
  }, [roster]);

  useEffect(() => { loadRoster(); loadAutoClosed(); }, [loadRoster, loadAutoClosed]);

  useEffect(() => {
    if (tab === 1) loadCareNotes();
    if (tab === 2) loadDailyUpdates();
  }, [tab, loadCareNotes, loadDailyUpdates]);

  const handleCheckIn = async (student_id) => {
    setError(''); setSuccess('');
    try {
      await teacherAPI.daycare.checkIn({ student_id });
      await loadRoster();
      setSuccess('Student checked in');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to check in');
    }
  };

  const handleCheckOut = async (session_id) => {
    setError(''); setSuccess('');
    try {
      await teacherAPI.daycare.checkOut({ session_id });
      await loadRoster();
      setSuccess('Student checked out');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to check out');
    }
  };

  const handleEditSave = async () => {
    setError('');
    try {
      await teacherAPI.daycare.editSession(editDialog.session.id, editForm);
      setEditDialog(null);
      await Promise.all([loadRoster(), loadAutoClosed()]);
      setSuccess('Session updated');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update session');
    }
  };

  const openEdit = (session) => {
    const toLocal = (ts) => {
      if (!ts) return '';
      const d = new Date(ts);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    setEditForm({ check_in: toLocal(session.check_in), check_out: toLocal(session.check_out) });
    setEditDialog({ session });
  };

  const openNotify = (student) => {
    setNotifyMsg('');
    setNotifyDialog({ student_id: student.student_id, student_name: student.student_name });
  };

  const handleSendNotification = async () => {
    if (!notifyMsg.trim()) return;
    setNotifySending(true);
    setError('');
    try {
      await teacherAPI.daycare.notifyParent({ student_id: notifyDialog.student_id, message: notifyMsg.trim() });
      setSuccess(`Parent of ${notifyDialog.student_name} notified successfully`);
      setNotifyDialog(null);
      setNotifyMsg('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to notify parent');
    } finally {
      setNotifySending(false);
    }
  };

  const handleAcknowledge = async (note) => {
    setNoteActionLoading(note.id + '_ack');
    setError('');
    try {
      await teacherAPI.daycare.acknowledgeCareNote(note.id);
      await loadCareNotes();
      setSuccess('Note acknowledged');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to acknowledge note');
    } finally {
      setNoteActionLoading(null);
    }
  };

  const openComplete = (note) => {
    setCompletionRemark('');
    setCompleteDialog({ note });
  };

  const handleCompleteSubmit = async () => {
    setNoteActionLoading(completeDialog.note.id + '_complete');
    setError('');
    try {
      await teacherAPI.daycare.completeCareNote(completeDialog.note.id, completionRemark);
      setCompleteDialog(null);
      await loadCareNotes();
      setSuccess('Note marked as completed — parent notified');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to complete note');
    } finally {
      setNoteActionLoading(null);
    }
  };

  const handleDailySubmit = async () => {
    if (!dailyForm.student_id || !dailyForm.content.trim()) {
      setError('Student and update content are required');
      return;
    }
    setDailySaving(true); setError(''); setSuccess('');
    try {
      if (dailyEditId) {
        await teacherAPI.dailyUpdates.edit(dailyEditId, dailyForm);
        setSuccess('Daily update saved');
        setDailyEditId(null);
      } else {
        await teacherAPI.dailyUpdates.post(dailyForm);
        setSuccess('Daily update posted');
      }
      setDailyForm(EMPTY_DAILY_FORM);
      await loadDailyUpdates();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save daily update');
    } finally {
      setDailySaving(false);
    }
  };

  const openDailyEdit = (u) => {
    setDailyEditId(u.id);
    setDailyForm({
      student_id:  u.student_id,
      update_date: u.update_date?.slice(0, 10) || today(),
      content:     u.content || '',
      meals:       u.meals || '',
      nap:         u.nap || '',
      mood:        u.mood || '',
      activities:  u.activities || '',
    });
  };

  const handleDailyDelete = async () => {
    setError('');
    try {
      await teacherAPI.dailyUpdates.remove(dailyDeleteId);
      setDailyDeleteId(null);
      setSuccess('Daily update deleted');
      await loadDailyUpdates();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete daily update');
    }
  };

  const checkedIn = roster.filter(r => r.is_checked_in);
  const notIn     = roster.filter(r => !r.is_checked_in);
  const pendingNotes = careNotes.filter(n => n.status === 'pending').length;

  return (
    <>
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 3 }, py: 4 }}>
        {/* Page title */}
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <DaycareIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Daycare</Typography>
            <Typography variant="body2" color="text.secondary">
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
            </Typography>
          </Box>
          <Box ml="auto" display="flex" gap={1} flexWrap="wrap">
            <Chip icon={<CheckedInIcon />} label={`${checkedIn.length} In`} color="success" size="small" />
            <Chip icon={<WaitingIcon />}   label={`${notIn.length} Waiting`} color="default" size="small" />
          </Box>
        </Box>

        {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {autoClosed.length > 0 && (
          <Alert
            severity="warning"
            icon={<AutoClosedIcon />}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              {autoClosed.length} check-out{autoClosed.length > 1 ? 's were' : ' was'} missed and auto-closed at 7:00 PM. Please review and correct the times below.
            </Typography>
            <Box display="flex" flexDirection="column" gap={0.5}>
              {autoClosed.map(s => (
                <Box key={s.id} display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                  <Typography variant="body2">
                    <strong>{s.student_name}</strong> — in {fmtTime(s.check_in)}, auto-closed {fmtTime(s.check_out)}
                  </Typography>
                  <Button size="small" startIcon={<EditIcon fontSize="small" />} onClick={() => openEdit(s)}>
                    Fix Time
                  </Button>
                </Box>
              ))}
            </Box>
          </Alert>
        )}

        <Paper sx={{ mb: 3, p: 0.75 }}>
          <ScrollableTabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            TabIndicatorProps={{ sx: { display: 'none' } }}
            sx={{
              minHeight: 0,
              '& .MuiTabs-flexContainer': { gap: 1 },
              '& .MuiTab-root': {
                minHeight: 0,
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
                py: 1,
                color: 'primary.main',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                '&:hover': {
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.2)
                },
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    bgcolor: 'primary.dark'
                  }
                }
              }
            }}
          >
            <Tab
              icon={<CheckedInIcon />}
              iconPosition="start"
              label={`Today's Roster (${roster.length})`}
            />
            <Tab
              icon={
                pendingNotes > 0
                  ? <Badge badgeContent={pendingNotes} color="error"><NoteIcon /></Badge>
                  : <NoteIcon />
              }
              iconPosition="start"
              label="Care Notes"
            />
            <Tab
              icon={<DailyUpdateIcon />}
              iconPosition="start"
              label="Daily Updates"
            />
          </ScrollableTabs>
        </Paper>

        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200' }}>
          <Box p={3}>
            {/* ——— ROSTER TAB ——— */}
            {tab === 0 && (
              <>
                {rosterLoading ? (
                  <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
                ) : roster.length === 0 ? (
                  <EmptyState
                    icon={DaycareIcon}
                    title="No students enrolled in daycare."
                    description="Ask the principal to enroll students in Daycare Management."
                  />
                ) : (
                  <Grid container spacing={2}>
                    {roster.map(r => (
                      <Grid item xs={12} sm={6} md={4} key={r.student_id}>
                        <Card
                          elevation={0}
                          sx={{
                            border: '2px solid',
                            borderColor: r.is_checked_in ? 'success.main' : 'grey.200',
                            transition: 'all 0.2s',
                          }}
                        >
                          <CardContent sx={{ pb: 1 }}>
                            <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                              <Badge
                                badgeContent={r.is_checked_in ? '✓' : null}
                                color="success"
                                overlap="circular"
                              >
                                <Avatar sx={{ bgcolor: r.is_checked_in ? 'success.main' : 'grey.300' }}>
                                  {r.student_name?.[0]?.toUpperCase()}
                                </Avatar>
                              </Badge>
                              <Box>
                                <Typography variant="subtitle2" fontWeight={700}>{r.student_name}</Typography>
                                <Typography variant="caption" color="text.secondary">{r.admission_number}</Typography>
                              </Box>
                            </Box>
                            {r.class_name && <Chip label={r.class_name} size="small" sx={{ mb: 1 }} />}
                            {r.is_checked_in && r.open_session && (
                              <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                                <ClockIcon fontSize="small" color="success" />
                                <Typography variant="body2" color="success.main" fontWeight={600}>
                                  In since {fmtTime(r.open_session.check_in)} ({elapsed(r.open_session.check_in)})
                                </Typography>
                              </Box>
                            )}
                          </CardContent>
                          <CardActions sx={{ pt: 0, px: 2, pb: 1.5, gap: 1, flexWrap: 'wrap' }}>
                            {r.is_checked_in ? (
                              <>
                                <Button
                                  size="small" variant="contained" color="warning"
                                  startIcon={<CheckOutIcon />}
                                  onClick={() => handleCheckOut(r.open_session.id)}
                                  sx={{ flex: 1 }}
                                >
                                  Check Out
                                </Button>
                                <Tooltip title="Edit session times">
                                  <IconButton size="small" onClick={() => openEdit(r.open_session)}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <Button
                                size="small" variant="contained" color="success"
                                startIcon={<CheckInIcon />}
                                onClick={() => handleCheckIn(r.student_id)}
                                sx={{ flex: 1 }}
                              >
                                Check In
                              </Button>
                            )}
                            <Tooltip title="Notify parent">
                              <IconButton
                                size="small"
                                onClick={() => openNotify(r)}
                                sx={{ color: 'primary.main' }}
                              >
                                <NotifyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </>
            )}

            {/* ——— CARE NOTES TAB ——— */}
            {tab === 1 && (
              <>
                {notesLoading ? (
                  <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
                ) : careNotes.length === 0 ? (
                  <EmptyState
                    icon={NoteIcon}
                    title="No care notes for daycare students today."
                  />
                ) : (
                  <Grid container spacing={2}>
                    {careNotes.map(note => (
                      <Grid item xs={12} sm={6} key={note.id}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            border: '1px solid',
                            borderColor: note.priority === 'urgent'
                              ? 'error.light'
                              : note.priority === 'high'
                                ? 'warning.light'
                                : 'grey.200',
                            bgcolor: note.priority === 'urgent'
                              ? 'error.50'
                              : note.priority === 'high'
                                ? 'warning.50'
                                : '#fff',
                          }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'primary.light' }}>
                                {note.student_name?.[0]?.toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight={700}>{note.student_name}</Typography>
                                <Typography variant="caption" color="text.secondary">{note.parent_name}</Typography>
                              </Box>
                            </Box>
                            <Box display="flex" gap={0.5} flexWrap="wrap" justifyContent="flex-end">
                              <Chip
                                label={note.priority}
                                size="small"
                                color={PRIORITY_COLOR[note.priority]}
                                icon={PRIORITY_ICON[note.priority]}
                              />
                              <Chip
                                label={note.status}
                                size="small"
                                color={STATUS_COLOR[note.status]}
                              />
                            </Box>
                          </Box>
                          <Typography variant="body2" sx={{ color: 'grey.800', lineHeight: 1.5 }}>
                            {note.note_text}
                          </Typography>
                          {note.status === 'completed' && note.completion_remark && (
                            <Box sx={{ mt: 1, p: 1, bgcolor: 'success.50', borderRadius: 1 }}>
                              <Typography variant="caption" color="success.dark" fontWeight={600}>Your remark: </Typography>
                              <Typography variant="caption" color="success.dark">{note.completion_remark}</Typography>
                            </Box>
                          )}
                          <Box display="flex" justifyContent="flex-end" gap={1} mt={1.5} flexWrap="wrap">
                            {note.status === 'pending' && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={noteActionLoading === note.id + '_ack' ? <CircularProgress size={14} /> : <AcknowledgeIcon />}
                                onClick={() => handleAcknowledge(note)}
                                disabled={!!noteActionLoading}
                              >
                                Acknowledge
                              </Button>
                            )}
                            {(note.status === 'pending' || note.status === 'acknowledged') && (
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={noteActionLoading === note.id + '_complete' ? <CircularProgress size={14} color="inherit" /> : <CompleteIcon />}
                                onClick={() => openComplete(note)}
                                disabled={!!noteActionLoading}
                              >
                                Mark Completed
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<NotifyIcon />}
                              onClick={() => openNotify({ student_id: note.student_id, student_name: note.student_name })}
                            >
                              Notify Parent
                            </Button>
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </>
            )}

            {/* ——— DAILY UPDATES TAB ——— */}
            {tab === 2 && (
              <>
                <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'grey.200' }}>
                  <Typography variant="subtitle1" fontWeight={700} mb={2}>
                    {dailyEditId ? 'Edit Daily Update' : 'Post Daily Update'}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select fullWidth label="Student" value={dailyForm.student_id}
                        onChange={e => setDailyForm(f => ({ ...f, student_id: e.target.value }))}
                        disabled={!!dailyEditId}
                      >
                        {roster.map(r => (
                          <MenuItem key={r.student_id} value={r.student_id}>
                            {r.student_name} ({r.admission_number})
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth type="date" label="Date" value={dailyForm.update_date}
                        onChange={e => setDailyForm(f => ({ ...f, update_date: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth multiline minRows={3} label="Update / Notes *"
                        placeholder="Describe the child's day, activities, observations…"
                        value={dailyForm.content}
                        onChange={e => setDailyForm(f => ({ ...f, content: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth label="Meals" placeholder="e.g. Ate lunch well, had snack"
                        value={dailyForm.meals} onChange={e => setDailyForm(f => ({ ...f, meals: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth label="Nap" placeholder="e.g. 1 hour nap at 1 PM"
                        value={dailyForm.nap} onChange={e => setDailyForm(f => ({ ...f, nap: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select fullWidth label="Mood" value={dailyForm.mood}
                        onChange={e => setDailyForm(f => ({ ...f, mood: e.target.value }))}
                      >
                        <MenuItem value="">— Not specified —</MenuItem>
                        {MOODS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth label="Activities" placeholder="e.g. Art, outdoor play, storytime"
                        value={dailyForm.activities} onChange={e => setDailyForm(f => ({ ...f, activities: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box display="flex" gap={1}>
                        <Button
                          variant="contained" startIcon={<SendIcon />}
                          onClick={handleDailySubmit} disabled={dailySaving}
                        >
                          {dailySaving ? 'Saving…' : dailyEditId ? 'Save Changes' : 'Post Update'}
                        </Button>
                        {dailyEditId && (
                          <Button variant="outlined" onClick={() => { setDailyEditId(null); setDailyForm(EMPTY_DAILY_FORM); }}>
                            Cancel
                          </Button>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>

                <Typography variant="subtitle1" fontWeight={700} mb={1}>Today's Updates</Typography>
                {dailyLoading ? (
                  <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
                ) : dailyUpdates.length === 0 ? (
                  <EmptyState
                    icon={DailyUpdateIcon}
                    title="No daily updates posted for daycare students today."
                  />
                ) : (
                  <Grid container spacing={2}>
                    {dailyUpdates.map(u => (
                      <Grid item xs={12} sm={6} key={u.id}>
                        <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'grey.200' }}>
                          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'primary.light' }}>
                                {u.student_name?.[0]?.toUpperCase()}
                              </Avatar>
                              <Typography variant="body2" fontWeight={700}>{u.student_name}</Typography>
                            </Box>
                            {u.mood && <Chip label={u.mood} size="small" />}
                          </Box>
                          <Typography variant="body2" sx={{ color: 'grey.800', lineHeight: 1.5, mb: 1 }}>
                            {u.content}
                          </Typography>
                          {(u.meals || u.nap || u.activities) && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mb: 1 }}>
                              {u.meals && <Typography variant="caption" color="text.secondary">Meals: {u.meals}</Typography>}
                              {u.nap && <Typography variant="caption" color="text.secondary">Nap: {u.nap}</Typography>}
                              {u.activities && <Typography variant="caption" color="text.secondary">Activities: {u.activities}</Typography>}
                            </Box>
                          )}
                          <Box display="flex" justifyContent="flex-end" gap={1}>
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => openDailyEdit(u)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => setDailyDeleteId(u.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Edit Session Dialog */}
      <MobileFormDialog
        open={!!editDialog}
        onClose={() => setEditDialog(null)}
        title="Edit Session Times"
        maxWidth="xs"
        actions={
          <>
            <Button onClick={() => setEditDialog(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleEditSave}>Save</Button>
          </>
        }
      >
        <TextField
          label="Check In" type="datetime-local" fullWidth sx={{ mb: 2, mt: 1 }}
          value={editForm.check_in}
          onChange={e => setEditForm(f => ({ ...f, check_in: e.target.value }))}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Check Out (leave blank if still in)" type="datetime-local" fullWidth
          value={editForm.check_out}
          onChange={e => setEditForm(f => ({ ...f, check_out: e.target.value }))}
          InputLabelProps={{ shrink: true }}
        />
      </MobileFormDialog>

      {/* Notify Parent Dialog */}
      <MobileFormDialog
        open={!!notifyDialog}
        onClose={() => setNotifyDialog(null)}
        title="Notify Parent"
        actions={
          <>
            <Button onClick={() => setNotifyDialog(null)} disabled={notifySending}>Cancel</Button>
            <Button
              variant="contained"
              startIcon={notifySending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              onClick={handleSendNotification}
              disabled={!notifyMsg.trim() || notifySending}
            >
              {notifySending ? 'Sending…' : 'Send'}
            </Button>
          </>
        }
      >
        {notifyDialog && (
          <Typography variant="body2" color="text.secondary" mb={2}>
            Send a message to the parent of <strong>{notifyDialog.student_name}</strong>.
            They will receive an in-app notification and an email.
          </Typography>
        )}
        <TextField
          autoFocus
          multiline
          rows={4}
          fullWidth
          label="Message to parent"
          placeholder="e.g. Your child had a great day! They are ready for pickup at 5 PM."
          value={notifyMsg}
          onChange={e => setNotifyMsg(e.target.value)}
          inputProps={{ maxLength: 500 }}
          helperText={`${notifyMsg.length}/500`}
        />
      </MobileFormDialog>

      {/* Complete Care Note Dialog */}
      <MobileFormDialog
        open={!!completeDialog}
        onClose={() => setCompleteDialog(null)}
        title="Mark as Completed"
        actions={
          <>
            <Button onClick={() => setCompleteDialog(null)} disabled={!!noteActionLoading}>Cancel</Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleCompleteSubmit}
              disabled={!!noteActionLoading}
              startIcon={noteActionLoading ? <CircularProgress size={14} color="inherit" /> : <CompleteConfirmIcon />}
            >
              Complete &amp; Notify Parent
            </Button>
          </>
        }
      >
        {completeDialog && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">Note for {completeDialog.note.student_name}:</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>{completeDialog.note.note_text}</Typography>
          </Box>
        )}
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Completion remark (optional)"
          placeholder="E.g., Gave cough syrup at 11 AM and 4 PM"
          value={completionRemark}
          onChange={e => setCompletionRemark(e.target.value)}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          The parent will receive an email and in-app notification when you mark this as completed.
        </Typography>
      </MobileFormDialog>

      {/* Delete Daily Update Confirmation */}
      <Dialog open={!!dailyDeleteId} onClose={() => setDailyDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Daily Update?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDailyDeleteId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDailyDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
